<script setup lang="ts">
    import { ref, computed, watch, toRef } from 'vue'

    import type { EmailSnippetReadModel } from '@Alternatives/Alternatives/Domain/readmodels'
    import type { ValidationError } from '@common/types'
    import type { EmailSenderViewModel } from '@Alternatives/Alternatives/Components/EmailSender'

    import { EmailSenderFormSubmitEventPayload } from '@Alternatives/Alternatives/Components/EmailSender'

    // ---------------- props / emits ----------------

    const props = defineProps<{
        viewModel: EmailSenderViewModel
    }>()

    const emit = defineEmits<{
        (e: 'form-submit', payload: EmailSenderFormSubmitEventPayload): void
    }>()

    // ---------------- shortcuts ----------------

    const vm = toRef(props, 'viewModel')

    // ---------------- derived (from VM) ----------------

    const opened = computed(() => vm.value?.opened ?? false)

    const sending = computed({
        get: () => vm.value.sending,
        set: val => { vm.value.sending = val }
    })

    const alternativeName = computed(() => vm.value.alternativeName)
    const emailSnippets = computed(() => vm.value.emailSnippets)

    const subjectError = computed(() =>
        vm.value.errors.find(e => e.ErrorCode.startsWith('Subject.'))?.Error ?? null
    )

    const bodyHtmlError = computed(() =>
        vm.value.errors.find(e => e.ErrorCode.startsWith('Body.'))?.Error ?? null
    )

    const replyToEmailError = computed(() =>
        vm.value.errors.find(e => e.ErrorCode.startsWith('ReplyTo.'))?.Error ?? null
    )

    const toEmailsError = computed(() =>
        vm.value.errors.find(e => e.ErrorCode.startsWith('To.'))?.Error ?? null
    )

    // ---------------- local state ----------------

    const subject = ref('')
    const bodyHtml = ref('')
    const replyToEmail = ref('')
    const toEmails = ref('')

    // ---------------- methods ----------------

    function insertEmailSnippet(content: string)
    {
        bodyHtml.value = `${bodyHtml.value} ${content}`.trim()
    }

    function handleFormSubmit()
    {
        vm.value.clearEmailValidationErrors()

        sending.value = true

        const to = toEmails.value
            .split(/[,;]/)
            .map(e => e.trim())
            .filter(Boolean)

        emit(
            'form-submit',
            new EmailSenderFormSubmitEventPayload(
                vm.value.alternativeId!,
                subject.value,
                replyToEmail.value,
                to,
                bodyHtml.value
            )
        )
    }

    function close()
    {
        vm.value.close()
    }

    // ---------------- watchers ----------------

    watch(opened, val =>
    {
        if (!val)
            return

        vm.value.clearEmailValidationErrors()

        sending.value = false
        subject.value = ''
        bodyHtml.value = ''
        toEmails.value = vm.value.defaultTo ?? ''
        replyToEmail.value = vm.value.defaultReplyTo ?? ''
    })
</script>


<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-lg">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Send email: {{alternativeName}}</h5>
                        <button @click="close" type="button" class="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <form id="email-sender-form" @submit.prevent="handleFormSubmit">
                            <div class="form-group">
                                <label>Send to:</label>
                                <input v-model.trim="toEmails" type="text" class="form-control" required />
                                <div class="error">{{toEmailsError}}</div>
                            </div>

                            <div class="form-group">
                                <label>Subject:</label>
                                <input type="text" v-model.trim="subject" class="form-control" required />
                                <div class="error">{{subjectError}}</div>
                            </div>

                            <div class="form-group">
                                <label>Reply to:</label>
                                <input type="email" v-model.trim="replyToEmail" class="form-control" required />
                                <div class="error">{{replyToEmailError}}</div>
                            </div>

                            <hr />
                            <div class="pull-right">
                                <div class="dropdown">
                                    <a class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown">
                                        Insert
                                    </a>
                                    <div class="dropdown-menu dropdown-menu-right">
                                        <a class="dropdown-item" v-for="s in emailSnippets" :key="s.Title" @click="insertEmailSnippet(s.Content)">
                                            {{s.Title}}
                                        </a>
                                    </div>
                                </div>
                            </div>
                            <textarea v-model.trim="bodyHtml" class="form-control " rows="5" placeholder="Type your message here..." required />
                            <div class="error">{{bodyHtmlError}}</div>
                        </form>

                    </div>

                    <div class="modal-footer">
                        <button type="button" @click="close" class="btn btn-outline-secondary">Cancel</button>
                        <button type="submit" form="email-sender-form" :disabled="sending" class="btn btn-primary">Send</button>
                    </div>

                </div>
            </div>
        </div>
    </div>
</template>
